import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import {
  Card,
  Button,
  Input,
  PageHeader,
  Spinner,
  EmptyState,
  Badge,
  Modal,
  Field,
  Select,
  InfoRow,
  ErrorBanner,
  formatCents,
  formatDate,
  type BadgeColor,
} from '../components/ui';

interface Invoice {
  id: string;
  invoiceNumber: string;
  contactId: string;
  status: 'draft' | 'sent' | 'paid' | 'void' | 'overdue' | 'partial';
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  currency: string;
  dueDate?: string;
  sentAt?: string;
  paidAt?: string;
  notes?: string;
  createdAt: string;
}

interface LineItem {
  id: string;
  invoiceId: string;
  description: string;
  quantity: number;
  unitPriceCents: number;
  totalCents: number;
}

interface InvoiceWithItems extends Invoice {
  lineItems: LineItem[];
}

interface Contact {
  id: string;
  firstName: string;
  lastName?: string;
  email?: string;
}

const statusColor: Record<Invoice['status'], BadgeColor> = {
  draft: 'gray',
  sent: 'blue',
  paid: 'green',
  void: 'gray',
  overdue: 'red',
  partial: 'yellow',
};

export function InvoicesPage() {
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const qc = useQueryClient();

  const listQ = useQuery({
    queryKey: ['invoices'],
    queryFn: () =>
      api.get<{ data: Invoice[]; meta: { total: number } }>('/invoices', { limit: '100' }).catch(() => ({
        data: [],
        meta: { total: 0 },
      })),
  });

  const invoices = listQ.data?.data ?? [];
  const outstanding = invoices
    .filter((i) => i.status !== 'paid' && i.status !== 'void')
    .reduce((sum, i) => sum + i.totalCents, 0);

  return (
    <div>
      <PageHeader
        title="Invoices"
        subtitle={`${invoices.length} total — ${formatCents(outstanding)} outstanding`}
        action={<Button onClick={() => setShowCreate(true)}>+ New invoice</Button>}
      />

      <Card style={{ padding: 0 }}>
        {listQ.isLoading ? (
          <Spinner />
        ) : invoices.length === 0 ? (
          <EmptyState
            title="No invoices yet"
            description="Create your first invoice to get paid."
            action={<Button onClick={() => setShowCreate(true)}>+ New invoice</Button>}
          />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr
                style={{
                  background: 'var(--color-surface-secondary)',
                  textAlign: 'left',
                  fontSize: 'var(--text-xs)',
                  color: 'var(--color-text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                <th style={cellStyle}>#</th>
                <th style={cellStyle}>Status</th>
                <th style={cellStyle}>Total</th>
                <th style={cellStyle}>Due</th>
                <th style={cellStyle}>Created</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr
                  key={inv.id}
                  onClick={() => setSelectedId(inv.id)}
                  style={{ cursor: 'pointer', borderBottom: '1px solid var(--color-border-primary)' }}
                >
                  <td style={cellStyle}>
                    <strong>{inv.invoiceNumber}</strong>
                  </td>
                  <td style={cellStyle}>
                    <Badge color={statusColor[inv.status]}>{inv.status}</Badge>
                  </td>
                  <td style={cellStyle}>{formatCents(inv.totalCents, inv.currency)}</td>
                  <td style={cellStyle}>{inv.dueDate ? formatDate(inv.dueDate) : '—'}</td>
                  <td style={cellStyle}>{formatDate(inv.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {showCreate && (
        <CreateInvoiceModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            qc.invalidateQueries({ queryKey: ['invoices'] });
            setShowCreate(false);
            setSelectedId(id);
          }}
        />
      )}

      {selectedId && <InvoiceDetail invoiceId={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

function CreateInvoiceModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [contactId, setContactId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState<{ description: string; quantity: number; unitPriceCents: number }[]>([
    { description: '', quantity: 1, unitPriceCents: 0 },
  ]);
  const [error, setError] = useState<string | null>(null);

  const contactsQ = useQuery({
    queryKey: ['contacts', 'all'],
    queryFn: () => api.get<{ data: Contact[] }>('/contacts', { limit: '200' }),
  });

  const createMut = useMutation({
    mutationFn: async (input: {
      contactId: string;
      dueDate?: string;
      notes?: string;
      lineItems: { description: string; quantity: number; unitPriceCents: number }[];
    }) => api.post<InvoiceWithItems>('/invoices', input),
    onSuccess: (inv) => onCreated(inv.id),
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed to create invoice'),
  });

  const subtotal = lineItems.reduce((s, li) => s + li.quantity * li.unitPriceCents, 0);

  function updateItem(idx: number, patch: Partial<(typeof lineItems)[number]>) {
    setLineItems((items) => items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const valid = lineItems.filter((li) => li.description.trim());
    if (!contactId) return setError('Pick a contact');
    if (valid.length === 0) return setError('Add at least one line item');
    createMut.mutate({
      contactId,
      dueDate: dueDate || undefined,
      notes: notes || undefined,
      lineItems: valid,
    });
  }

  const contacts = contactsQ.data?.data ?? [];

  return (
    <Modal onClose={onClose} title="New invoice" width={640}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <Field label="Contact *">
          <select
            value={contactId}
            onChange={(e) => setContactId(e.target.value)}
            style={selectStyle}
          >
            <option value="">Select a contact…</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.firstName} {c.lastName ?? ''} {c.email ? `(${c.email})` : ''}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Due date">
          <Input value={dueDate} onChange={setDueDate} type="date" />
        </Field>

        <div>
          <div style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>
            Line items
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {lineItems.map((li, idx) => (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 60px 100px 32px', gap: 'var(--space-2)', alignItems: 'center' }}>
                <Input value={li.description} onChange={(v) => updateItem(idx, { description: v })} placeholder="Description" />
                <Input
                  value={li.quantity}
                  onChange={(v) => updateItem(idx, { quantity: Number(v) || 1 })}
                  type="number"
                  min={1}
                />
                <Input
                  value={(li.unitPriceCents / 100).toFixed(2)}
                  onChange={(v) => updateItem(idx, { unitPriceCents: Math.round((Number(v) || 0) * 100) })}
                  type="number"
                  step="0.01"
                  min={0}
                />
                <button
                  type="button"
                  onClick={() => setLineItems((items) => items.filter((_, i) => i !== idx))}
                  style={{ background: 'transparent', border: 'none', color: 'var(--color-text-tertiary)', cursor: 'pointer', fontSize: 'var(--text-lg)' }}
                  aria-label="Remove line"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <Button
            variant="ghost"
            onClick={() => setLineItems((items) => [...items, { description: '', quantity: 1, unitPriceCents: 0 }])}
            style={{ marginTop: 'var(--space-2)' }}
          >
            + Add line
          </Button>
        </div>

        <Field label="Notes">
          <Input value={notes} onChange={setNotes} />
        </Field>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: 'var(--space-3)',
            background: 'var(--color-surface-secondary)',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
          }}
        >
          <span>Subtotal</span>
          <span>{formatCents(subtotal)}</span>
        </div>

        <ErrorBanner error={error} />
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end', marginTop: 'var(--space-2)' }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={createMut.isPending}>
            {createMut.isPending ? 'Creating…' : 'Create invoice'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function InvoiceDetail({ invoiceId, onClose }: { invoiceId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['invoices', invoiceId],
    queryFn: () => api.get<InvoiceWithItems>(`/invoices/${invoiceId}`),
  });

  const sendMut = useMutation({
    mutationFn: () => api.post(`/invoices/${invoiceId}/send`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoices'] }),
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed to send'),
  });

  const voidMut = useMutation({
    mutationFn: () => api.post(`/invoices/${invoiceId}/void`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoices'] }),
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed to void'),
  });

  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState<'cash' | 'check' | 'card' | 'bank' | 'other'>('cash');

  const recordMut = useMutation({
    mutationFn: (input: { amountCents: number; method: string }) =>
      api.post('/payments', {
        invoiceId,
        contactId: q.data!.contactId,
        amountCents: input.amountCents,
        currency: q.data!.currency,
        method: input.method,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      setPayAmount('');
    },
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed to record payment'),
  });

  const textToPayMut = useMutation({
    mutationFn: () => api.post<{ token: string; url: string }>('/payment-links', { invoiceId }),
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed to generate link'),
  });

  if (q.isLoading || !q.data) {
    return (
      <Modal onClose={onClose} title="Invoice">
        <Spinner />
      </Modal>
    );
  }

  const inv = q.data;
  const canSend = inv.status === 'draft';
  const canVoid = inv.status !== 'void' && inv.status !== 'paid';
  const canPay = inv.status !== 'paid' && inv.status !== 'void';
  const pdfUrl = `${(import.meta.env.VITE_API_URL as string | undefined) ?? 'https://frontdesk-api.blancorunning.workers.dev'}/api/v1/invoices/${inv.id}/pdf`;

  return (
    <Modal onClose={onClose} title={`Invoice ${inv.invoiceNumber}`} width={720}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-3)' }}>
          <InfoRow label="Status">
            <Badge color={statusColor[inv.status]}>{inv.status}</Badge>
          </InfoRow>
          <InfoRow label="Total">{formatCents(inv.totalCents, inv.currency)}</InfoRow>
          <InfoRow label="Due">{inv.dueDate ? formatDate(inv.dueDate) : '—'}</InfoRow>
          <InfoRow label="Sent">{inv.sentAt ? formatDate(inv.sentAt) : '—'}</InfoRow>
        </div>

        <div>
          <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, margin: '0 0 var(--space-2)' }}>Line items</h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--color-text-tertiary)' }}>
                <th style={{ padding: 'var(--space-2) 0' }}>Description</th>
                <th style={{ padding: 'var(--space-2) 0', textAlign: 'right' }}>Qty</th>
                <th style={{ padding: 'var(--space-2) 0', textAlign: 'right' }}>Price</th>
                <th style={{ padding: 'var(--space-2) 0', textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {inv.lineItems.map((li) => (
                <tr key={li.id} style={{ borderTop: '1px solid var(--color-border-primary)' }}>
                  <td style={{ padding: 'var(--space-2) 0' }}>{li.description}</td>
                  <td style={{ padding: 'var(--space-2) 0', textAlign: 'right' }}>{li.quantity}</td>
                  <td style={{ padding: 'var(--space-2) 0', textAlign: 'right' }}>
                    {formatCents(li.unitPriceCents, inv.currency)}
                  </td>
                  <td style={{ padding: 'var(--space-2) 0', textAlign: 'right' }}>
                    {formatCents(li.totalCents, inv.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-4)', marginTop: 'var(--space-3)', fontWeight: 600 }}>
            <span>Total</span>
            <span>{formatCents(inv.totalCents, inv.currency)}</span>
          </div>
        </div>

        {canPay && (
          <Card style={{ padding: 'var(--space-3)', background: 'var(--color-surface-secondary)' }}>
            <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: 'var(--space-2)' }}>Record payment</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 'var(--space-2)', alignItems: 'end' }}>
              <Field label="Amount">
                <Input value={payAmount} onChange={setPayAmount} type="number" step="0.01" placeholder="0.00" />
              </Field>
              <Field label="Method">
                <Select
                  value={payMethod}
                  onChange={setPayMethod}
                  options={[
                    { value: 'cash', label: 'Cash' },
                    { value: 'check', label: 'Check' },
                    { value: 'card', label: 'Card (manual)' },
                    { value: 'bank', label: 'Bank transfer' },
                    { value: 'other', label: 'Other' },
                  ]}
                />
              </Field>
              <Button
                onClick={() => {
                  const cents = Math.round((Number(payAmount) || 0) * 100);
                  if (cents <= 0) return setError('Enter an amount');
                  recordMut.mutate({ amountCents: cents, method: payMethod });
                }}
                disabled={recordMut.isPending}
              >
                {recordMut.isPending ? 'Saving…' : 'Record'}
              </Button>
            </div>
          </Card>
        )}

        <ErrorBanner error={error} />

        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          {canSend && (
            <Button onClick={() => sendMut.mutate()} disabled={sendMut.isPending}>
              {sendMut.isPending ? 'Sending…' : 'Send to client'}
            </Button>
          )}
          <Button variant="secondary" onClick={() => window.open(pdfUrl, '_blank')}>
            Download PDF
          </Button>
          {canPay && (
            <Button
              variant="secondary"
              onClick={() => textToPayMut.mutate()}
              disabled={textToPayMut.isPending}
            >
              {textToPayMut.isPending
                ? 'Generating…'
                : textToPayMut.data
                ? 'Copy pay link'
                : 'Generate pay link'}
            </Button>
          )}
          {textToPayMut.data && (
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', alignSelf: 'center' }}>
              {textToPayMut.data.url}
            </span>
          )}
          {canVoid && (
            <Button variant="danger" onClick={() => voidMut.mutate()} disabled={voidMut.isPending}>
              {voidMut.isPending ? 'Voiding…' : 'Void'}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}

const cellStyle: React.CSSProperties = {
  padding: 'var(--space-3)',
  fontSize: 'var(--text-sm)',
  color: 'var(--color-text-primary)',
};

const selectStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  border: '1px solid var(--color-border-primary)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-sm)',
  color: 'var(--color-text-primary)',
  background: 'var(--color-surface-primary)',
};
